import { setBrand, type Brand } from './brand.ts';
import { loadTaxRates, resetTaxRates } from './taxes.ts';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { allowLanguages, setFormatLocale } from '../i18n/index.ts';
import { ApiError, idleFor, request, setUnauthorizedHandler, tokenStore } from './api.ts';
import { setCurrency } from './format.ts';
import { inApp, native, registerPhone } from './native.ts';
import { dropStaleCopy, go, otherAccounts, restore, unsentSales } from './accounts.ts';

export interface Me {
  tenantId: string;
  userId: string;
  name: string;
  roles: string[];
  permissions: string[];
  /** Missing when the business is not on a plan. */
  plan?: { state: 'trial' | 'active' | 'past_due' | 'read_only' | 'cancelled'; name: string | null; until: string | null; read_only: boolean };
  /** Signed in as ERP PRO support, with the owner's consent, until then. */
  support?: { by: string; until: string };
  /** The languages server administration turned on for the business, its own first. */
  languages?: string[];
  /** The business's country and how it writes dates and numbers (its country pack). */
  country?: string;
  formats?: { locale: string; firstDay: 0 | 1 };
  /** This sign-in was confirmed with a code from the authenticator app (or on a remembered device). */
  mfa?: boolean;
  /** Password and authenticator app still to be set up, by then; blocked: only the set-up is open. */
  security?: { setup: true; until: string; blocked: boolean };
  /** The partner's brand when a partner brought the business (white-label). */
  brand?: Brand | null;
  /** This sign-in ends after so many minutes without use (the roles that see money and settings). */
  idleMinutes?: number;
}

const IDLE_NOTE = 'erp.signedOutIdle';
/** Minutes of the sign-in that just ended for being unused, for the sign-in page to say so once. */
export function takeIdleNote(): number | null {
  try {
    const v = Number(sessionStorage.getItem(IDLE_NOTE));
    sessionStorage.removeItem(IDLE_NOTE);
    return v > 0 ? v : null;
  } catch {
    return null;
  }
}
const noteIdle = (minutes: number) => {
  try {
    sessionStorage.setItem(IDLE_NOTE, String(minutes));
  } catch {
    // The sign-in page then just asks to sign in.
  }
};
export interface Tenant {
  id: string;
  slug: string;
  name: string;
  currency: string;
  languages?: string[];
}

interface SessionState {
  me: Me | null;
  tenant: Tenant | null;
  loading: boolean;
  can: (permission: string) => boolean;
  signIn: (tenant: string, username: string, secret: string, extra?: { code?: string; remember?: boolean }) => Promise<void>;
  /** Finishes a sign-in through the business's provider with the one-time ticket it sent back. */
  signInWithTicket: (ticket: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Reads who is signed in again (after the person changed their sign-in). */
  refresh: () => Promise<void>;
}

/** The token of a device remembered for 30 days, per business and person. */
const trustKey = (slug: string, username: string) => `erp.trusted.${slug}.${username.toLowerCase()}`;
const readTrust = (key: string) => {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const Ctx = createContext<SessionState | null>(null);
const TENANT_KEY = 'erp.tenant';
/** Who is signed in on this device, so the till still opens while the server cannot be reached. */
const ME_KEY = 'erp.me';
const remember = (key: string, value: unknown) => {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not remembered on this device.
  }
};

/**
 * Keeps the business's languages for the next start (i18n reads them before anything shows)
 * and, when the language on screen is no longer one of them, shows the app again in one that is.
 */
const applyLanguages = (languages: string[] | undefined) => {
  if (!languages) return;
  try {
    const saved = JSON.parse(localStorage.getItem(TENANT_KEY) ?? 'null') as Tenant | null;
    if (saved && saved.languages?.join() !== languages.join()) localStorage.setItem(TENANT_KEY, JSON.stringify({ ...saved, languages }));
  } catch {
    // Not remembered; the next sign-in brings them again.
  }
  if (allowLanguages(languages)) window.location.reload();
};

const readTenant = (): Tenant | null => {
  try {
    return JSON.parse(localStorage.getItem(TENANT_KEY) ?? 'null');
  } catch {
    return null;
  }
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(readTenant);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      // Ended by the server for being unused (the computer slept past the limit): the sign-in page says why.
      let was: Me | null = null;
      try {
        was = JSON.parse(localStorage.getItem(ME_KEY) ?? 'null') as Me | null;
      } catch {
        // Not known: no note.
      }
      if (was?.idleMinutes && idleFor() >= was.idleMinutes * 60_000) noteIdle(was.idleMinutes);
      tokenStore.set(null);
      remember(ME_KEY, null);
      setMe(null);
    });
    if (!tokenStore.get()) {
      setLoading(false);
      return;
    }
    request<Me>('GET', '/api/me')
      .then((m) => {
        remember(ME_KEY, m);
        setFormatLocale(m.formats?.locale ?? null);
        setMe(m);
        applyLanguages(m.languages);
      })
      .catch((err) => {
        // No connection: carry on as the person last seen here, for the offline till.
        if (err instanceof ApiError && err.code === 'offline') {
          try {
            const cached = JSON.parse(localStorage.getItem(ME_KEY) ?? 'null') as Me | null;
            if (cached) {
              setFormatLocale(cached.formats?.locale ?? null);
              return setMe(cached);
            }
          } catch {
            // fall through
          }
        }
        tokenStore.set(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tenant) setCurrency(tenant.currency);
  }, [tenant]);

  // The business's tax rates, ready before a screen makes a new line with the usual one.
  const signedIn = me ? `${me.tenantId}:${me.userId}` : null;
  useEffect(() => {
    if (signedIn) void loadTaxRates(true);
    else resetTaxRates();
  }, [signedIn]);

  // In the phone app, alerts use the same sign-in while the app is closed, and end with it.
  useEffect(() => {
    if (loading || !inApp()) return;
    if (me) {
      native.session(tokenStore.get(), me.userId, me.name);
      void registerPhone();
    } else native.signedOut();
  }, [me, loading]);

  /** Keeps a new sign-in: its token, who it is, and the business. */
  const started = useCallback((r: { token: string; session: Me; tenant: Tenant }) => {
    tokenStore.set(r.token);
    remember(ME_KEY, r.session);
    try {
      localStorage.setItem(TENANT_KEY, JSON.stringify(r.tenant));
    } catch {
      // Not remembered on this device; sign-in still works.
    }
    dropStaleCopy();
    setTenant(r.tenant);
    setMe(r.session);
    applyLanguages(r.tenant.languages);
    // The business's country formats come with the full profile.
    void request<Me>('GET', '/api/me')
      .then((m) => {
        remember(ME_KEY, m);
        setFormatLocale(m.formats?.locale ?? null);
        setMe(m);
      })
      .catch(() => undefined);
  }, []);

  const signIn = useCallback(async (slug: string, username: string, secret: string, extra: { code?: string; remember?: boolean } = {}) => {
    const trust = trustKey(slug.trim().toLowerCase(), username.trim());
    const r = await request<{ token: string; session: Me; tenant: Tenant; device_token?: string }>('POST', '/api/auth/login', {
      tenant: slug.trim().toLowerCase(),
      username: username.trim(),
      secret,
      device: navigator.userAgent.slice(0, 100),
      ...(extra.code ? { code: extra.code, remember: !!extra.remember } : {}),
      device_token: readTrust(trust),
    });
    if (r.device_token) {
      try {
        localStorage.setItem(trust, r.device_token);
      } catch {
        // Not remembered: the code is asked next time too.
      }
    }
    started(r);
  }, [started]);

  const signInWithTicket = useCallback(
    async (ticket: string) => started(await request<{ token: string; session: Me; tenant: Tenant }>('POST', '/api/auth/sso/exchange', { ticket })),
    [started],
  );


  const signOut = useCallback(async () => {
    await request('POST', '/api/auth/logout').catch(() => {});
    tokenStore.set(null);
    remember(ME_KEY, null);
    // Another business kept on this device comes back on screen instead of the sign-in page, unless this
    // business still has offline sales to send: they stay here for its next sign-in.
    const next = otherAccounts()[0];
    if (next && !unsentSales() && restore(next.key)) return go();
    setFormatLocale(null);
    setMe(null);
  }, []);

  const refresh = useCallback(async () => {
    const m = await request<Me>('GET', '/api/me');
    remember(ME_KEY, m);
    setMe(m);
  }, []);

  // An office sign-in left unused ends here too, so an unattended screen does not keep showing the books.
  const idleMinutes = me?.idleMinutes;
  useEffect(() => {
    if (!idleMinutes) return;
    const timer = window.setInterval(() => {
      if (idleFor() < idleMinutes * 60_000) return;
      window.clearInterval(timer);
      noteIdle(idleMinutes);
      void signOut();
    }, 15_000);
    return () => window.clearInterval(timer);
  }, [idleMinutes, signOut]);

  // The business's partner brand while signed in; the address's own after signing out.
  useEffect(() => setBrand(me?.brand ?? null), [me]);

  const value = useMemo<SessionState>(
    () => ({ me, tenant, loading, can: (p) => !!me?.permissions.includes(p), signIn, signInWithTicket, signOut, refresh }),
    [me, tenant, loading, signIn, signInWithTicket, signOut, refresh],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The signed-in business's address name, or null on a guest's page (no one signed in). */
export function useTenantSlug(): string | null {
  return useContext(Ctx)?.tenant?.slug ?? null;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}

/** Device settings that belong to this screen, not to the person. */
export const device = {
  get register(): string | null {
    try {
      return localStorage.getItem('erp.register');
    } catch {
      return null;
    }
  },
  set register(id: string | null) {
    try {
      if (id) localStorage.setItem('erp.register', id);
      else localStorage.removeItem('erp.register');
    } catch {
      // ignore
    }
  },
  /** The location this device works at; a till takes its register's. */
  get location(): string | null {
    try {
      return localStorage.getItem('erp.location');
    } catch {
      return null;
    }
  },
  set location(id: string | null) {
    try {
      if (id) localStorage.setItem('erp.location', id);
      else localStorage.removeItem('erp.location');
    } catch {
      // ignore
    }
  },
  get station(): string | null {
    try {
      return localStorage.getItem('erp.station');
    } catch {
      return null;
    }
  },
  set station(id: string | null) {
    try {
      if (id) localStorage.setItem('erp.station', id);
      else localStorage.removeItem('erp.station');
    } catch {
      // ignore
    }
  },
};
